/**
 * Global keyboard shortcuts for telephony.
 *
 * Shortcuts are filtered out while the user is typing (input/textarea/contenteditable)
 * except for Ctrl+Shift+D (dialer), which works everywhere.
 *
 * Audit (2026-04-17): existing single-key shortcuts in this app ('e', 'i', Cmd/Ctrl+Enter)
 * live inside ThreadList, ThreadDetail and WhatsAppChat — all guarded by isTyping
 * checks on their end. Ctrl+Shift+D is free. If a conflict appears later, swap to Alt+D.
 */

type CallStateForKeys = 'idle' | 'ringing' | 'active' | 'other';

export interface KeyboardHandlers {
  onAnswer: () => void;
  onReject: () => void;
  onHangup: () => void;
  onToggleMute: () => void;
  onOpenDialPad: () => void;
  getActiveCallState: () => CallStateForKeys;
}

function isTyping(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') return true;
  if (t.isContentEditable) return true;
  return false;
}

export function installKeyboardShortcuts(h: KeyboardHandlers): () => void {
  function onKeyDown(e: KeyboardEvent) {
    // Ctrl+Shift+D opens the dialer anywhere (also inside inputs).
    if (e.ctrlKey && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
      e.preventDefault();
      h.onOpenDialPad();
      return;
    }

    if (isTyping(e)) return;

    const state = h.getActiveCallState();

    if (state === 'ringing') {
      if (e.key === 'a' || e.key === 'A' || e.key === 'Enter') {
        e.preventDefault();
        h.onAnswer();
        return;
      }
      if (e.key === 'Escape' || e.key === 'h' || e.key === 'H') {
        e.preventDefault();
        h.onReject();
        return;
      }
    }

    if (state === 'active') {
      if (e.key === 'm' || e.key === 'M') {
        e.preventDefault();
        h.onToggleMute();
        return;
      }
      if (e.key === 'Escape' || e.key === 'h' || e.key === 'H') {
        e.preventDefault();
        h.onHangup();
        return;
      }
      if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        h.onHangup();
        return;
      }
    }
  }

  window.addEventListener('keydown', onKeyDown);
  return () => window.removeEventListener('keydown', onKeyDown);
}
